import { useEffect, useState } from 'react'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import { BarChart3, Briefcase, Building2, ChevronDown, CreditCard, LayoutDashboard, LogOut, Settings, Sparkles, Tag, UserCog, Users } from 'lucide-react'
import { useB2BAuthStore } from '../../stores/useB2BAuthStore'
import { api } from '../../api/client'
import { useCookieConsentStore } from '../../stores/useCookieConsentStore'
import OnboardingTour from './OnboardingTour'
import { isOnboardingSeen } from '../../utils/b2bOnboarding'

function navLinkClassName(isActive) {
  return `relative flex items-center gap-3 px-3 min-h-[44px] rounded-xl text-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 ${
    isActive
      ? 'bg-accent/10 text-ink-1 font-semibold before:absolute before:left-0 before:top-2 before:bottom-2 before:w-0.5 before:rounded-full before:bg-accent'
      : 'font-medium text-ink-2 hover:bg-canvas hover:text-ink-1'
  }`
}

function SidebarLink({ item }) {
  const NavIcon = item.icon
  return (
    <NavLink to={item.to} className={({ isActive }) => navLinkClassName(isActive)}>
      <NavIcon className="w-4 h-4" />
      <span>{item.label}</span>
    </NavLink>
  )
}

/**
 * Company nav item with a chevron that lazily expands the workspace's companies
 * (fetched on first expand). Clicking a company opens its page; the parent label
 * links to the companies index.
 */
function CompanyNavGroup() {
  const [expanded, setExpanded] = useState(false)
  const [companies, setCompanies] = useState(null)

  useEffect(() => {
    if (!expanded || companies !== null) return
    let cancelled = false
    api.get('/api/b2b/companies')
      .then((d) => { if (!cancelled) setCompanies(d) })
      .catch(() => { if (!cancelled) setCompanies([]) })
    return () => { cancelled = true }
  }, [expanded, companies])

  return (
    <div>
      <div className="flex items-center gap-1">
        <NavLink to="/b2b/companies" className={({ isActive }) => `flex-1 min-w-0 ${navLinkClassName(isActive)}`}>
          <Building2 className="w-4 h-4" />
          <span>Company</span>
        </NavLink>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-label={expanded ? 'Collapse companies' : 'Expand companies'}
          aria-expanded={expanded}
          className="p-2 rounded-lg text-ink-3 hover:bg-canvas hover:text-ink-1 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {expanded && (
        <div className="ml-4 mt-1 space-y-0.5 border-l border-black/5 pl-2">
          {companies === null ? (
            <p className="px-3 py-2 text-[12px] text-ink-3">Loading…</p>
          ) : companies.length === 0 ? (
            <p className="px-3 py-2 text-[12px] text-ink-3">No companies yet</p>
          ) : (
            companies.map((c) => (
              <NavLink
                key={c.id}
                to={`/b2b/companies/${c.id}`}
                className={({ isActive }) =>
                  `block px-3 py-2 rounded-lg text-[13px] truncate transition ${
                    isActive ? 'bg-accent/10 text-ink-1 font-semibold' : 'text-ink-2 hover:bg-canvas hover:text-ink-1'
                  }`
                }
              >
                {c.name}
              </NavLink>
            ))
          )}
        </div>
      )}
    </div>
  )
}

/**
 * AppShell — sidebar + header layout для всех приватных страниц.
 * Mounts OnboardingTour once on first visit (any /b2b/* page) and exposes
 * a "Replay tour" button in the sidebar footer.
 */
export default function AppShell({ children }) {
  const navigate = useNavigate()
  const member = useB2BAuthStore((s) => s.member)
  const workspace = useB2BAuthStore((s) => s.workspace)
  const logout = useB2BAuthStore((s) => s.logout)

  // Tour state. Auto-opens on first visit (gated by localStorage),
  // can be reopened via the "Replay tour" button.
  const [tourOpen, setTourOpen] = useState(false)
  const [tourReplay, setTourReplay] = useState(false)

  useEffect(() => {
    // Only auto-show after we have a hydrated member (avoids opening it
    // briefly on the way to /b2b/auth when token is gone).
    if (!member) return
    if (!isOnboardingSeen()) {
      setTourOpen(true)
      setTourReplay(false)
    }
  }, [member])

  const initials = (member?.name || member?.email || '?')
    .split(/\s+|@/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('')

  // Candidates (cross-job roster) is Phase 2 — hidden from the sidebar until the
  // feature lands so the nav doesn't lead to a dead-end placeholder. The route
  // still exists for any deep links / future enablement.
  const navTop = { to: '/b2b/dashboard', label: 'Dashboard', icon: LayoutDashboard }
  const navRest = [
    { to: '/b2b/jobs', label: 'Jobs', icon: Briefcase },
    { to: '/b2b/candidates', label: 'Candidates', icon: Users },
    { to: '/b2b/settings/members', label: 'Members', icon: UserCog },
    { to: '/b2b/pricing', label: 'Pricing', icon: Tag },
    { to: '/b2b/billing', label: 'Billing', icon: CreditCard },
    { to: '/b2b/settings/workspace', label: 'Settings', icon: Settings },
  ]

  const handleLogout = () => {
    logout()
    navigate('/b2b/auth', { replace: true })
  }

  const handleReplayTour = () => {
    setTourReplay(true)
    setTourOpen(true)
  }

  return (
    <div className="min-h-screen flex">
      <aside className="hidden md:flex w-64 flex-col bg-white border-r border-black/5">
        <div className="px-6 py-6 border-b border-black/5">
          <p className="text-[11px] font-bold uppercase tracking-wider text-ink-3">Workspace</p>
          <p className="mt-1 text-base font-bold text-ink-1 truncate">{workspace?.name || '—'}</p>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          <SidebarLink item={navTop} />
          <CompanyNavGroup />
          {navRest.map((item) => <SidebarLink key={item.to} item={item} />)}
          <NavLink
            to="/b2b/quizzes"
            className={({ isActive }) =>
              `mt-2 flex items-center gap-3 px-3 min-h-[44px] rounded-xl text-sm font-bold text-white bg-accent hover:bg-accent-hover transition ${
                isActive ? 'ring-2 ring-accent-hover ring-offset-2' : ''
              }`
            }
          >
            <BarChart3 className="w-4 h-4" />
            <span>Quizzes</span>
          </NavLink>
        </nav>

        <div className="px-3 py-3 border-t border-black/5 space-y-1">
          <button
            type="button"
            onClick={handleReplayTour}
            className="w-full flex items-center gap-3 px-3 min-h-[44px] rounded-xl text-sm font-medium text-ink-2 hover:bg-canvas hover:text-ink-1 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
          >
            <Sparkles className="w-4 h-4 text-accent" />
            <span>Replay tour</span>
          </button>

          <div className="flex items-center gap-3 px-3 py-2 rounded-xl">
            <div className="w-9 h-9 rounded-full bg-accent/10 flex items-center justify-center text-accent text-xs font-bold shrink-0">
              {initials || '?'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-ink-1 truncate">{member?.name || member?.email}</p>
              <p className="text-[11px] text-ink-3 capitalize">{member?.role || 'member'}</p>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              className="w-11 h-11 inline-flex items-center justify-center rounded-lg text-ink-3 hover:text-ink-1 hover:bg-canvas transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
              aria-label="Sign out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 bg-canvas">
        {/* Mobile top bar */}
        <header className="md:hidden bg-white border-b border-black/5 px-4 py-3 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-ink-3">Workspace</p>
            <p className="text-sm font-bold text-ink-1">{workspace?.name || '—'}</p>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handleReplayTour}
              className="text-ink-3 p-2"
              aria-label="Replay tour"
            >
              <Sparkles className="w-4 h-4 text-accent" />
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="text-ink-3 p-2"
              aria-label="Sign out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </header>

        <div className="animate-fade-in-up">{children}</div>

        <B2BFooter />
      </main>

      {tourOpen && (
        <OnboardingTour
          replay={tourReplay}
          onComplete={() => {
            setTourOpen(false)
            setTourReplay(false)
          }}
        />
      )}
    </div>
  )
}

function B2BFooter() {
  const openManager = useCookieConsentStore((s) => s.openManager)
  const links = [
    { label: 'Business Terms', to: '/b2b/terms' },
    { label: 'DPA', to: '/b2b/dpa' },
    { label: 'Privacy', to: '/privacy' },
    { label: 'Sub-processors', to: '/privacy/sub-processors' },
    { label: 'AI Notice', to: '/ai' },
    { label: 'AUP', to: '/aup' },
    { label: 'Cookie Policy', to: '/cookies' },
  ]
  return (
    <footer className="mt-12 border-t border-black/5 px-6 md:px-10 py-6 text-[12px] text-ink-3 flex flex-wrap items-center gap-x-5 gap-y-2 justify-between">
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {links.map((l) => (
          <Link key={l.to} to={l.to} target="_blank" className="hover:text-ink-1">
            {l.label}
          </Link>
        ))}
        <button
          type="button"
          onClick={openManager}
          className="hover:text-ink-1"
        >
          Cookie preferences
        </button>
      </div>
      <span>© 2026 Yevhenii Lisechko</span>
    </footer>
  )
}
